import React, { useCallback, useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import { styled, alpha, useTheme } from "@mui/material/styles";
import type { TransitionProps } from "@mui/material/transitions";

import { ALPHA } from "@shell/styles/designTokens";

const DRAWER_WIDTH = 380;

const SlideTransition = React.forwardRef(function SlideTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const DrawerContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  right: 0,
  width: DRAWER_WIDTH,
  height: "100vh",
  zIndex: theme.zIndex.drawer,
  backgroundColor: theme.palette.background.paper,
  borderLeft: `1px solid ${alpha(theme.palette.divider, ALPHA.DIVIDER_SECTION)}`,
  display: "flex",
  flexDirection: "column",
  boxShadow: `-4px 0 24px ${alpha(theme.palette.common.black, 0.08)}`,
}));

const Backdrop = styled(Box)(({ theme }) => ({
  position: "fixed",
  inset: 0,
  zIndex: theme.zIndex.drawer - 1,
  backgroundColor: alpha(theme.palette.common.black, 0.4),
}));

const ScrollContent = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "16px 20px",
});

const StickyFooter = styled(Box)(({ theme }) => ({
  padding: "12px 20px",
  borderTop: `1px solid ${alpha(theme.palette.divider, ALPHA.DIVIDER_ITEM)}`,
  backgroundColor: theme.palette.background.paper,
}));

export interface DrawerShellProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  isSubmitting?: boolean;
  isSubmitDisabled?: boolean;
  error?: string | null;
  children: React.ReactNode;
}

export function DrawerShell({
  title,
  isOpen,
  onClose,
  onSubmit,
  submitLabel = "Submit",
  isSubmitting = false,
  isSubmitDisabled = false,
  error = null,
  children,
}: DrawerShellProps): React.ReactElement | null {
  const theme = useTheme();
  const [isDirty, setIsDirty] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Track focus origin for restoration
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
      // Focus first focusable element in drawer
      setTimeout(() => {
        const first = drawerRef.current?.querySelector<HTMLElement>(
          "input, button, select, textarea, [tabindex]:not([tabindex='-1'])",
        );
        first?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = drawerRef.current?.querySelectorAll<HTMLElement>(
        'input, button, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  const attemptClose = useCallback(() => {
    if (isDirty) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        attemptClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, attemptClose]);

  // Restore focus on close
  useEffect(() => {
    if (!isOpen && triggerRef.current) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Reset dirty state when drawer opens
  useEffect(() => {
    if (isOpen) setIsDirty(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <Backdrop onClick={attemptClose} />
      <SlideTransition in={isOpen} timeout={300}>
        <DrawerContainer ref={drawerRef} role="dialog" aria-label={title}>
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, ALPHA.DIVIDER_ITEM)}`,
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            <IconButton
              size="small"
              onClick={attemptClose}
              aria-label="Close drawer"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* Error banner */}
          {error && (
            <Alert severity="error" sx={{ mx: 2.5, mt: 2 }} onClose={() => {}}>
              {error}
            </Alert>
          )}

          {/* Scrollable content */}
          <ScrollContent>
            <DrawerDirtyContext.Provider value={{ isDirty, setIsDirty }}>
              {children}
            </DrawerDirtyContext.Provider>
          </ScrollContent>

          {/* Sticky footer with submit */}
          {onSubmit && (
            <StickyFooter>
              <Button
                variant="contained"
                fullWidth
                onClick={onSubmit}
                disabled={isSubmitting || isSubmitDisabled}
                sx={{ textTransform: "none" }}
              >
                {isSubmitting ? "Submitting…" : submitLabel}
              </Button>
            </StickyFooter>
          )}
        </DrawerContainer>
      </SlideTransition>

      {/* Dirty-close confirmation */}
      <Dialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You have unsaved changes. Are you sure you want to close?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirm(false)}>Keep editing</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setShowConfirm(false);
              setIsDirty(false);
              onClose();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Context for child forms to mark the drawer as dirty. */
export const DrawerDirtyContext = React.createContext<{
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
}>({
  isDirty: false,
  setIsDirty: () => {},
});

/** Hook for child forms to mark the drawer dirty on input. */
export function useDrawerDirty() {
  const ctx = React.useContext(DrawerDirtyContext);
  return ctx;
}
